import { Col, Form, Modal, Row } from "react-bootstrap";
import { Button } from "../../../../components/Formularios/Buttons/Button";
import { mdiCheckCircle, mdiKeyboardReturn } from "@mdi/js";
import { useEffect, useState, useCallback } from "react";
import { TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Box } from "@mui/material";
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { endpointPrincipal } from "../../../../api/endPoints/endpointPrincipal/endpointPrincipal";
import { useNavigate } from "react-router-dom";

interface AtividadeModalProps {
  showModal: boolean;
  acao?: string;
  onClose: () => void;
  onSubmit: (formData: any) => void;
  dadosDescricao?: string;
  dadosId?: number;
  ticketData?: any; // Dados do ticket a ser criado
  isNewTicket?: boolean; // Indica se é um novo ticket ou uma atividade para um ticket existente
}

const DropzoneArea = styled('div')(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border .3s ease-in-out',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&.dragActive': {
    borderColor: theme.palette.success.main,
  }
}));

const FileList = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
});

const FileItem = styled('li')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
}));

const ModalAtividade: React.FC<AtividadeModalProps> = ({
  showModal,
  acao,
  onClose,
  onSubmit,
  dadosDescricao,
  dadosId,
  ticketData,
  isNewTicket = false
}) => {
  const [descricao, setDescricao] = useState("");
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const carregarDados = useCallback(() => {
    setDescricao(dadosDescricao || "");
  }, [dadosDescricao]);

  useEffect(() => {
    if (showModal) {
      carregarDados();
    }
  }, [showModal, carregarDados]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Se estiver apenas visualizando, feche o modal
    if (acao === "Visualizar") {
      onClose();
      return;
    }
    
    // Validação básica
    if (!descricao) {
      toast.error('Preencha a descrição obrigatória');
      return;
    }

    try {
      setLoading(true);
      
      if (isNewTicket && ticketData) {
        // Criar um novo ticket com a atividade
        const formData = new FormData();
        
        // Garantir que o título esteja presente
        if (!ticketData.titulo) {
          toast.error('O título do ticket é obrigatório');
          return;
        }
        
        formData.append('titulo', ticketData.titulo);
        
        // Adicionar outros campos do ticket
        if (ticketData.grupo) formData.append('grupo', ticketData.grupo);
        if (ticketData.status) formData.append('status', ticketData.status);
        if (ticketData.prioridade) formData.append('prioridade', ticketData.prioridade);
        
        // Adicionar agente_id se existir
        if (ticketData.agente_id) {
          formData.append('agente_id', ticketData.agente_id.toString());
        }
        
        // Garantir que a data de vencimento esteja presente
        const dataVencimento = ticketData.dataVencimento || new Date().toISOString();
        formData.append('dataVencimento', dataVencimento);
        
        // Adicionar o tipo do ticket (campo obrigatório)
        if (ticketData.tipo) {
          formData.append('tipo', ticketData.tipo);
        } else {
          formData.append('tipo', 'SUPORTE'); // Valor padrão para o tipo do ticket
        }
        
        // Adicionar a descrição
        formData.append('descricao', descricao);
        
        // Adicionar arquivos
        if (arquivos && arquivos.length > 0) {
          arquivos.forEach(arquivo => {
            // Verificar a extensão do arquivo
            const fileExtension = arquivo.name.split('.').pop()?.toLowerCase();
            
            // Se for um arquivo .rar ou .zip, verificar se o tipo MIME está correto
            if (fileExtension === 'rar' && arquivo.type !== 'application/x-rar-compressed' && arquivo.type !== 'application/vnd.rar') {
              // Criar um novo objeto File com o tipo MIME correto
              const correctedFile = new File([arquivo], arquivo.name, { type: 'application/x-rar-compressed' });
              formData.append('arquivos', correctedFile);
            } else if (fileExtension === 'zip' && arquivo.type !== 'application/zip') {
              // Criar um novo objeto File com o tipo MIME correto
              const correctedFile = new File([arquivo], arquivo.name, { type: 'application/zip' });
              formData.append('arquivos', correctedFile);
            } else {
              formData.append('arquivos', arquivo);
            }
          });
        }
        
        // Enviar para a API de tickets
        const response = await endpointPrincipal.post('/tickets', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        toast.success('Ticket criado com sucesso!');
        onClose();
        // Navegar para a lista de tickets em vez de visualizar o ticket criado
        setTimeout(() => navigate('/tickets'), 2000);
      } else {
        // Comportamento original - apenas retorna os dados para o componente pai
        onSubmit({
          descricao,
          arquivos
        });
      }
      
      // Limpar os campos após o envio bem-sucedido
      setDescricao("");
      setArquivos([]);
    } catch (error: any) {
      console.error('Erro ao criar ticket:', error);
      toast.error(`Erro ao criar ticket: ${error.response?.data?.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Corrigir o tipo MIME dos arquivos .rar e .zip se necessário
    const filesWithCorrectMimeType = acceptedFiles.map(file => {
      // Verificar a extensão do arquivo
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      // Se for um arquivo .rar com tipo MIME incorreto ou vazio
      if (fileExtension === 'rar' && file.type !== 'application/x-rar-compressed' && file.type !== 'application/vnd.rar') {
        // Criar um novo objeto File com o tipo MIME correto
        return new File([file], file.name, { type: 'application/x-rar-compressed' });
      }
      
      // Se for um arquivo .zip com tipo MIME incorreto ou vazio
      if (fileExtension === 'zip' && file.type !== 'application/zip') {
        // Criar um novo objeto File com o tipo MIME correto
        return new File([file], file.name, { type: 'application/zip' });
      }
      
      return file;
    });
    
    setArquivos(prev => [...prev, ...filesWithCorrectMimeType]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf'],
      'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.mkv'],
      'application/zip': ['.zip'],
      'application/x-rar-compressed': ['.rar'],
      'application/vnd.rar': ['.rar'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt']
    }
  });

  const removeFile = (index: number) => {
    setArquivos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Modal 
      show={showModal} 
      className="modal-full-width"
      size="xl" 
      keyboard={false}
      centered
    >
      <Modal.Header className="bg-white">
        <Modal.Title>
          {isNewTicket ? 'Criar Novo Ticket' : 
           acao === "Visualizar" ? 'Visualizar Atividade' :
           acao === "Editar" ? 'Editar Atividade' : 'Incluir Atividade'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="bg-white">
          <Box sx={{ width: "100%" }}>
            <Row>
              <Col>
                <TextField
                  fullWidth
                  sx={{ mb: 3 }}
                  label="Descrição *"
                  multiline
                  rows={4}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
            </Row>
            {acao !== "Visualizar" && (
              <Row>
                <Col>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel shrink>Arquivos</InputLabel>
                    <DropzoneArea
                      {...getRootProps()}
                      className={isDragActive ? 'dragActive' : ''}
                    >
                      <input {...getInputProps()} />
                      <p>Arraste e solte arquivos aqui, ou clique para selecionar</p>
                      <p style={{ fontSize: '0.8rem', color: '#666' }}>
                        Formatos aceitos: imagens (jpeg, jpg, png), PDF, planilhas (xlsx, xls), documentos (docx, doc), texto (txt), vídeos (mp4, avi, mov, wmv, mkv) e arquivos comprimidos (zip, rar)
                      </p>
                      {isDragActive && (
                        <p style={{ color: 'green' }}>Solte os arquivos aqui...</p>
                      )}
                    </DropzoneArea>
                    
                    {arquivos.length > 0 && (
                      <FileList>
                        {arquivos.map((file, index) => (
                          <FileItem key={index}>
                            <span>{file.name}</span>
                            <Button
                              type="button"
                              className="btn btn-light text-danger bg-white border-0 p-1"
                              descricaoBotao=""
                              icone="mdiClose"
                              onclick={() => removeFile(index)}
                            />
                          </FileItem>
                        ))}
                      </FileList>
                    )}
                  </FormControl>
                </Col>
              </Row>
            )}
          </Box>
        </Modal.Body>
        <Modal.Footer className="bg-white">
          <Button
            type="button"
            className="btn btn-outline-secondary rounded-2 d-flex align-items-center gap-1"
            descricaoBotao="Cancelar"
            icone={mdiKeyboardReturn}
            onclick={onClose}
          />
          {acao !== "Visualizar" && (
            <Button
              type="submit"
              className="btn btn-success rounded-2 d-flex align-items-center gap-1"
              descricaoBotao="Salvar"
              icone={mdiCheckCircle}
            />
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalAtividade; 